import reportJobService from './reportJobService'
import { MAX_RUN_MS } from '@config/queueConfig'

/**
 * Shared plumbing for routing a report run through the async job system (spec 055).
 *
 * Used by the specialized (CSV/service) and accounting report clients so a heavy
 * report runs as a background job — it lands in the "My Reports" tray and dodges
 * the ~100s Cloudflare edge timeout — instead of blocking the run request.
 */

// Enabled by the build flag OR a per-session localStorage override, so we can verify
// on QA before flipping it on for everyone (set localStorage 'cb:reportJobsSpecialized'='1').
export const specializedJobsOn = () =>
  import.meta.env.VITE_REPORT_JOBS_SPECIALIZED === 'true' ||
  (typeof localStorage !== 'undefined' && localStorage.getItem('cb:reportJobsSpecialized') === '1')

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

/*
 * Spec 131 — the states a job can END in without a result, besides `failed`.
 *
 * This loop used to exit on `ready` or `failed` only. cb-api writes `cancelled`
 * (jobs/store.js cancelJob — "Stop waiting", or a cancel from the queue) and
 * treats `expired` as terminal beside them (the same list, store.js:389), so a job
 * that ended either way was polled every 1.5s for as long as the page stayed open.
 * Before spec 131 that was merely wasteful; now that a page keeps polling a
 * superseded run to terminal instead of abandoning it, a missing terminal would
 * keep that loop alive forever. Plain language, because the page shows it.
 */
// A Map, not an object literal: the key is a server-supplied string, and an
// object lookup would answer for inherited names ('constructor', 'toString').
export const ENDED_WITHOUT_RESULT = new Map([
  ['cancelled', 'This report run was cancelled before it finished.'],
  ['expired', 'This report run expired before its result could be read. Run the report again.'],
])

/*
 * Spec 176 (CC-54206) — a STATUS READ that fails in transit is not a failed RUN.
 *
 * Both run loops (ReportView's generic loop and `runReportAsJob` below) used to let
 * one failed `GET /reports/jobs/:id` end the run. Measured on QA 2026-10-13 (report
 * 121, jobs c01f75f9 and 78839912): the page's own status read was held past axios's
 * 30s timeout while the Queue badge and the bell each re-read the same job every 5s,
 * and the page then said "This report took too long to return. Narrow the date range
 * or add a filter" — about a run that finished, correctly, seconds later. The timeout
 * was of ONE read of the run's state; it said nothing about the run.
 *
 * So a read that never got an answer — an axios timeout, a dropped connection, a 5xx,
 * a 408/429 — leaves the run's outcome UNKNOWN, and the loop reads again. Only the
 * server's own answer ends a run: `failed` / `cancelled` / `expired`, or a definitive
 * 4xx (a 404 for a dismissed run, a 403, a 410), which still throw exactly as before.
 */
const TRANSIENT_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK', 'ECONNRESET'])

/** Did this status read fail without the server deciding anything about the run? */
export function isTransientStatusError(err) {
  if (!err || typeof err !== 'object') return false
  if (TRANSIENT_CODES.has(err.code)) return true
  const status = err.response?.status
  if (typeof status === 'number') return status >= 500 || status === 408 || status === 429
  // An axios error that never got a response at all (offline tab, connection reset).
  return err.isAxiosError === true
}

/**
 * How long a run's loop keeps reading through failed status reads before it stops
 * waiting: the job's own lifetime (`MAX_RUN_MS`, after which cb-api ends it itself)
 * plus one period of the every-2-minutes cron, by which the server has recorded that end. Past this the
 * page cannot learn anything a further read would tell it — the Queue can.
 */
export const STATUS_LOST_AFTER_MS = MAX_RUN_MS + 2 * 60 * 1000

/**
 * What the page says when it stops waiting. NOT a failure and NOT "took too long":
 * the last thing the server said was that the run is going, and the Queue is where its
 * result will be. Plain words with no "time out" in them — `userFacingError` rewrites
 * any timeout-shaped string into the "narrow the date range" advice this replaces.
 */
export const STILL_RUNNING_LEAD = 'This report is still running in the background, but this page stopped getting updates on it.'
export const STILL_RUNNING_TAIL = ' to see its result when it finishes.'
/** The same, once the server has said `ready` but the result read did not answer. */
export const FINISHED_UNREAD_LEAD = 'This report finished, but this page could not load its result.'
export const FINISHED_UNREAD_TAIL = ' to see it.'
// The plain-text forms (the specialized pages render an error STRING). The report page
// renders LEAD + a link reading "Open the Queue" + TAIL, which is the same prose.
export const STILL_RUNNING_SENTENCE = `${STILL_RUNNING_LEAD} Open the Queue${STILL_RUNNING_TAIL}`
export const FINISHED_UNREAD_SENTENCE = `${FINISHED_UNREAD_LEAD} Open the Queue${FINISHED_UNREAD_TAIL}`

const lostError = (sentence, cause, finished) =>
  Object.assign(new Error(sentence), { name: 'JobStatusLostError', jobStatusLost: true, finished, cause })

/** A run the page stopped following — distinct from a run that failed. */
export const isJobStatusLost = (err) => err?.jobStatusLost === true

/**
 * One status read inside a run's loop. Returns the job; or `null` when the read failed
 * in transit and the run is still inside its lifetime (read again); or, past that,
 * throws a `JobStatusLostError` carrying `STILL_RUNNING_SENTENCE`. Every other failure
 * is rethrown untouched, so a 404/410/403 still reads the way it did.
 */
export async function readRunStatus(jobId, { startedAt, now = Date.now } = {}) {
  try {
    return await reportJobService.status(jobId)
  } catch (err) {
    if (!isTransientStatusError(err)) throw err
    if (startedAt != null && now() - startedAt > STATUS_LOST_AFTER_MS) throw lostError(STILL_RUNNING_SENTENCE, err, false)
    return null
  }
}

/**
 * The result read of a run the server has already called `ready`. A transient failure
 * here is not "took too long" either — the run finished — so it says so and points at
 * the Queue, whose "View report" reads the same stored copy again.
 */
export async function readFinishedResult(read) {
  try {
    return await read()
  } catch (err) {
    if (isTransientStatusError(err)) throw lostError(FINISHED_UNREAD_SENTENCE, err, true)
    throw err
  }
}

/** Every run poll sleeps this long — one constant, which `jobPollGuard` (spec 150) keys on. */
export const RUN_POLL_MS = 1500

/**
 * Create a report job, poll to completion, and return the endpoint's stored result
 * (the same `{ data: … }` shape the direct call returns). `payload` is forwarded to
 * reportJobService.create — for a replay job it carries the `endpoint` { method, path, body }.
 *
 * No AbortSignal, deliberately (spec 131 §5): a superseded run is polled to its
 * terminal state exactly as before and its answer is dropped by the page's
 * latest-run gate. cb-api advances a job one step per poll, so stopping the polls
 * would park it in `running` against the firm-wide in-flight cap.
 *
 * Spec 176: a status read that fails in transit is read again (see `readRunStatus`).
 */
export async function runReportAsJob(payload) {
  const startedAt = Date.now()
  const created = await reportJobService.create(payload)
  if (created.state !== 'ready') {
    for (;;) {
      await sleep(RUN_POLL_MS)
      const st = await readRunStatus(created.jobId, { startedAt })
      if (!st) continue // the read failed in transit: the run is not over
      if (st.state === 'ready') break
      if (st.state === 'failed') throw new Error(st.error || 'The report failed.')
      if (ENDED_WITHOUT_RESULT.has(st.state)) throw new Error(ENDED_WITHOUT_RESULT.get(st.state))
    }
  }
  return readFinishedResult(() => reportJobService.result(created.jobId))
}
